import useUpdateServicePlanSubscriptionAddress from '@/api/mutations/servicePlans/useUpdateServicePlanSubscriptionAddress';
import useGetCustomerAddresses from '@/api/queries/customers/useGetCustomerAddresses';
import {
  Button,
  Combobox,
  Modal,
  ModalProps,
  OptionGroup,
  SkeletonLoader,
} from '@payaca/ambrosia';
import { useDebounce } from '@payaca/hooks';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useCallback, useMemo, useState } from 'react';

type SubscriptionAddress = {
  publicId: string;
  fullLocalAddress?: string | null;
};

interface IProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  subscriptionId: string;
  customerId: string;
  currentAddress?: SubscriptionAddress | null;
}

const NO_ADDRESS_VALUE = '__no-address__';
const PAGE_SIZE = 25;

const SubscriptionAddressModal: FC<IProps> = (props) => {
  const { isOpen, onClose, subscriptionId, customerId, currentAddress } = props;

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  useDebounce(
    () => {
      setDebouncedSearchQuery(searchQuery);
    },
    300,
    [searchQuery]
  );

  const {
    addresses: paginatedAddresses,
    isLoading: isLoadingAddresses,
    isFetching: isFetchingAddresses,
  } = useGetCustomerAddresses(
    customerId,
    { searchTerm: debouncedSearchQuery || undefined },
    { limit: PAGE_SIZE, offset: 0 }
  );

  const { mutate: updateAddress, isPending } =
    useUpdateServicePlanSubscriptionAddress();

  const isSearching = isFetchingAddresses && !isLoadingAddresses;

  const [comboBoxOptions, comboBoxGroups] = useMemo(() => {
    const customerAddresses = (paginatedAddresses?.items ?? []).filter(
      (address) => !!address.fullLocalAddress
    );

    const addressOptions = customerAddresses.map((address) => ({
      groupId: 'customerAddresses',
      label: address.fullLocalAddress!,
      value: address.publicId,
    }));

    addressOptions.push({
      groupId: 'other',
      label: 'No address',
      value: NO_ADDRESS_VALUE,
    });

    const groups: OptionGroup[] = [
      ...(customerAddresses.length > 0
        ? [{ id: 'customerAddresses', label: 'Customer Addresses' }]
        : []),
      { id: 'other', label: 'Other' },
    ];

    return [addressOptions, groups];
  }, [paginatedAddresses]);

  const handleAddressChange = useCallback(
    (value: string) => {
      const addressId = value === NO_ADDRESS_VALUE ? null : value;
      updateAddress(
        {
          subscriptionId,
          addressId,
        },
        {
          onSuccess: () => {
            onClose?.();
          },
        }
      );
    },
    [subscriptionId, updateAddress, onClose]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <Modal title="Subscription Address" isOpen onClose={onClose}>
      <Modal.Body>
        {isLoadingAddresses ? (
          <div className="flex flex-col gap-4">
            <SkeletonLoader.Title />
            <SkeletonLoader.Text className="w-1/4" />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="typography:prose">
              <p className="supporting-body">
                Select an address for this subscription from the customer's
                addresses.
              </p>
            </div>

            <div className="relative">
              <Combobox<string, false>
                value={currentAddress?.publicId}
                optionGroups={comboBoxGroups}
                disabled={isPending}
                placeholder="Select an address"
                inputPlaceholder="Type to search"
                options={comboBoxOptions}
                customButtonContent={currentAddress?.fullLocalAddress}
                onChange={handleAddressChange}
                query={searchQuery}
                setQuery={setSearchQuery}
              />
              {isSearching && (
                <div className="absolute right-10 top-1/2 -translate-y-1/2">
                  <UntitledIcon
                    name="loading-02.3"
                    className="h-4 w-4 animate-spin text-gray-400"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};

export default SubscriptionAddressModal;
